const MAX_RECORDING_BYTES = 90 * 1024 * 1024;
const MAX_CHUNK_BYTES = Math.floor(5.4 * 1024 * 1024);
const MAX_CHUNKS = 4096;

const encodeEnvelope = (metadata, blobs) => {
  const json = new TextEncoder().encode(JSON.stringify(metadata));
  const header = new Uint8Array(4);
  new DataView(header.buffer).setUint32(0, json.byteLength);
  return new Blob([header, json, ...blobs], { type: 'application/octet-stream' });
};

export function buildRecordingUpload(sessionId, actualDurationSeconds, chunks) {
  const ordered = [...chunks].sort((a, b) => a.chunkIndex - b.chunkIndex);
  if (!ordered.length || ordered.length > MAX_CHUNKS || ordered.some((chunk, index) =>
    chunk.chunkIndex !== index || !chunk.blob.size || chunk.blob.size > MAX_CHUNK_BYTES)) {
    throw new Error('recording.storageError');
  }
  if (!Number.isSafeInteger(actualDurationSeconds) || actualDurationSeconds < 0 || actualDurationSeconds > 8 * 60 * 60) {
    throw new Error('recording.sizeLimit');
  }
  const envelope = encodeEnvelope({
    sessionId, actualDurationSeconds,
    chunks: ordered.map(({ chunkIndex, blob }) => ({ chunkIndex, byteLength: blob.size, contentType: blob.type })),
  }, ordered.map(chunk => chunk.blob));
  if (envelope.size > MAX_RECORDING_BYTES) throw new Error('recording.sizeLimit');
  return envelope;
}

// Once streaming fails, the recording remains local and completion uses one
// HTTP upload. A send is acknowledged only after the server has saved its bytes.
export default class RecordingTransport {
  constructor({ sessionId, request, onFallback, WebSocketClass = window.WebSocket }) {
    this.sessionId = sessionId;
    this.request = request;
    this.onFallback = onFallback;
    this.WebSocketClass = WebSocketClass;
    this.mode = 'websocket';
    this.pending = new Map();
    this.closed = false;
  }

  async getEndpoints() {
    return this.request('/api/recording/transport', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: this.sessionId }),
    });
  }

  fallback() {
    if (this.mode === 'fallback' || this.closed) return;
    this.mode = 'fallback';
    clearInterval(this.heartbeat);
    this.pending.forEach(({ resolve, timeout }) => { clearTimeout(timeout); resolve(false); });
    this.pending.clear();
    this.socket?.close();
    this.onFallback?.();
  }

  async connect() {
    if (this.closed || this.mode === 'fallback') return false;
    try {
      const { webSocketUrl } = await this.getEndpoints();
      if (!this.WebSocketClass) throw new Error('WebSocket unavailable');
      if (this.closed) return false;
      const socket = new this.WebSocketClass(webSocketUrl);
      this.socket = socket;
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('WebSocket connection timed out')), 10000);
        socket.onopen = () => { clearTimeout(timeout); resolve(); };
        socket.onerror = () => { clearTimeout(timeout); reject(new Error('WebSocket connection failed')); this.fallback(); };
        socket.onclose = () => { clearTimeout(timeout); reject(new Error('WebSocket closed')); this.fallback(); };
        socket.onmessage = ({ data }) => {
          let message;
          try { message = JSON.parse(data); } catch (_) { this.fallback(); return; }
          if (message.type === 'pong') { this.lastPongAt = Date.now(); return; }
          if (message.type !== 'ack' || message.ok !== true) { this.fallback(); return; }
          const pending = this.pending.get(message.chunkIndex);
          if (pending) {
            clearTimeout(pending.timeout);
            this.pending.delete(message.chunkIndex);
            pending.resolve(true);
          }
        };
      });
      if (this.closed || this.mode !== 'websocket') return false;
      this.lastPongAt = Date.now();
      this.heartbeat = setInterval(() => {
        if (Date.now() - this.lastPongAt > 60000) { this.fallback(); return; }
        try { socket.send(JSON.stringify({ type: 'ping' })); } catch (_) { this.fallback(); }
      }, 20000);
      return true;
    } catch (_) {
      this.fallback();
      return false;
    }
  }

  async sendChunk(chunkIndex, blob) {
    if (this.closed || this.mode !== 'websocket') return false;
    if (this.socket?.readyState !== 1 || this.socket.bufferedAmount > 2 * MAX_CHUNK_BYTES || blob.size > MAX_CHUNK_BYTES) {
      this.fallback();
      return false;
    }
    return new Promise(resolve => {
      const timeout = setTimeout(() => this.fallback(), 30000);
      this.pending.set(chunkIndex, { resolve, timeout });
      try { this.socket.send(encodeEnvelope({ chunkIndex, contentType: blob.type }, [blob])); }
      catch (_) { this.fallback(); }
    });
  }

  async uploadRecording(actualDurationSeconds, chunks) {
    const body = buildRecordingUpload(this.sessionId, actualDurationSeconds, chunks);
    // Mint a fresh scoped token; the socket may have failed hours earlier.
    const endpoints = await this.getEndpoints();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10 * 60 * 1000);
    try {
      const response = await fetch(endpoints.fallbackUploadUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body, signal: controller.signal,
      });
      if (!response.ok) throw new Error('recording.uploadError');
      const acknowledgement = await response.json();
      if (acknowledgement.ok !== true || acknowledgement.sessionId !== this.sessionId ||
          acknowledgement.finalChunkIndex !== chunks.length - 1) throw new Error('recording.uploadError');
    } catch (_) {
      throw new Error('recording.uploadError');
    } finally {
      clearTimeout(timeout);
    }
  }

  close() {
    this.closed = true;
    clearInterval(this.heartbeat);
    this.pending.forEach(({ resolve, timeout }) => { clearTimeout(timeout); resolve(false); });
    this.pending.clear();
    this.socket?.close();
  }
}
